import { urlSchema } from '@kanbu/schema/contracts';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Input, Label } from '@utima/ui';
import { Search } from 'lucide-react';
import { memo, useCallback, useRef, useState, type ReactNode } from 'react';

import { trpc } from '@/services/trpc';

import { AddExternalDocument } from './AddExternalDocument';
import type { AddSourcePanelHandle } from './addSourcePanelHandle';
import { AddWebsites, type WebsitePanelHandle } from './AddWebsites';

export type AddOnlineSourceProps = {
  limitDisplay?: ReactNode;
  chatIds: string[];
  availableForAllAgents: boolean;
  setOpen: (open: boolean) => void;
  defaultUrl?: string;
  limitReached: boolean;
  projectedLimitReached: boolean;
  onPendingCountChange: (count: number) => void;
  folderId?: string | null;
};

export const AddOnlineSource = memo(function AddOnlineSource(
  props: AddOnlineSourceProps,
) {
  const { t } = useLingui();
  const { onPendingCountChange } = props;
  const [url, setUrl] = useState(props.defaultUrl ?? '');
  const [documentUrl, setDocumentUrl] = useState<string>();
  const [classifying, setClassifying] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [count, setCount] = useState(0);
  const version = useRef(0);
  const websiteRef = useRef<WebsitePanelHandle>(null);
  const documentRef = useRef<AddSourcePanelHandle>(null);
  const parsed = urlSchema.safeParse(url);
  const { refetch } = trpc.documents.classifyUrl.useQuery(
    { url: parsed.success ? parsed.data : url },
    { enabled: false, retry: false },
  );
  const pending = useCallback(
    (value: number) => {
      setCount(value);
      onPendingCountChange(value);
    },
    [onPendingCountChange],
  );
  const busy = classifying || searching;
  const search = async () => {
    if (!parsed.success || submitting) return;
    const run = ++version.current;
    websiteRef.current?.invalidate();
    pending(0);
    setError(undefined);
    setClassifying(true);
    try {
      const result = await refetch();
      if (run !== version.current) return;
      if (result.error) throw result.error;
      if (result.data?.kind === 'rss' || result.data?.kind === 'google_drive') {
        throw new Error(t`Use the RSS or Google Drive tab for this link.`);
      }
      setClassifying(false);
      if (result.data?.kind === 'document') {
        setDocumentUrl(parsed.data);
        pending(1);
      } else {
        setDocumentUrl(undefined);
        await websiteRef.current?.search(parsed.data);
      }
    } catch (failure) {
      if (run === version.current)
        setError(
          failure instanceof Error
            ? failure.message
            : t`Search failed. Try again.`,
        );
    } finally {
      if (run === version.current) setClassifying(false);
    }
  };

  return (
    <div className='flex shrink-0 flex-col gap-2'>
      <div className='flex flex-col gap-2 sm:flex-row sm:items-end'>
        <div className='flex w-full min-w-0 flex-1 flex-col gap-1'>
          <Label htmlFor='online-source-url'>
            <Trans>Website or document URL</Trans>
          </Label>
          <Input
            id='online-source-url'
            data-cta='admin-add-online-source-url-input'
            placeholder='https://example.com'
            value={url}
            disabled={submitting}
            onChange={event => {
              version.current++;
              setUrl(event.target.value);
              setClassifying(false);
              setError(undefined);
              setDocumentUrl(undefined);
              websiteRef.current?.invalidate();
              pending(0);
            }}
            onKeyDown={event => {
              if (event.key === 'Enter' && !busy) {
                event.preventDefault();
                void search();
              }
            }}
          />
        </div>
        <Button
          data-cta='admin-add-online-source-action-button'
          variant='secondary'
          disabled={!parsed.success || busy || submitting}
          onClick={search}
          loading={busy}
          icon={<Search className='size-4' />}
        >
          <Trans>Search</Trans>
        </Button>
      </div>
      <div className={documentUrl ? 'hidden' : 'flex shrink-0 flex-col'}>
        <AddWebsites
          {...props}
          ref={websiteRef}
          embedded
          defaultUrl={undefined}
          searching={classifying}
          searchError={error}
          active={!documentUrl}
          onSearchingChange={setSearching}
          onPendingCountChange={pending}
          onSubmittingChange={setSubmitting}
        />
      </div>
      {documentUrl && (
        <>
          <AddExternalDocument
            {...props}
            key={documentUrl}
            ref={documentRef}
            embedded
            defaultUrl={documentUrl}
            onPendingCountChange={pending}
            onSubmittingChange={setSubmitting}
          />
          {error && (
            <p role='alert' className='text-sm text-danger'>
              {error}
            </p>
          )}
        </>
      )}
      <div className='sticky -bottom-4 z-10 -mx-4 -mb-4 flex flex-wrap items-center justify-end gap-3 border-t border-border bg-background px-4 py-3 sm:-bottom-5 sm:-mx-5 sm:-mb-5 sm:px-5'>
        <div className='mr-auto min-w-0'>{props.limitDisplay}</div>
        <span className='text-xs text-muted-fg'>
          <Trans>{count} selected</Trans>
        </span>
        <Button
          data-cta='admin-add-online-source-add-button'
          disabled={
            busy ||
            submitting ||
            !!error ||
            !count ||
            props.limitReached ||
            props.projectedLimitReached
          }
          loading={submitting}
          onClick={() =>
            documentUrl
              ? documentRef.current?.submit()
              : websiteRef.current?.submit()
          }
        >
          <Trans>Add</Trans> ({count})
        </Button>
      </div>
    </div>
  );
});
